import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  Plus,
  Trash2,
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Filter,
  XCircle,
} from 'lucide-react';
import {
  docProcessingApi,
  DomainTerm,
  SynonymMapping,
  SynonymMappingUpsertRequest,
} from '../../services/docProcessingApi';

const PAGE_SIZE = 25;

type SortField = 'term' | 'synonym' | 'relationship_type' | 'confidence' | 'source' | 'verified' | 'usage_count';
type SortDir = 'asc' | 'desc';

interface ColumnFilters {
  term: string;
  synonym: string;
  relationship_type: string;
  confidence: string;
  source: string;
  verified: string; // 'all' | 'yes' | 'no'
  usage_count: string;
}

const defaultFilters: ColumnFilters = {
  term: '',
  synonym: '',
  relationship_type: '',
  confidence: '',
  source: '',
  verified: 'all',
  usage_count: '',
};

export default function SynonymMappingsEditor() {
  const [mappings, setMappings] = useState<SynonymMapping[]>([]);
  const [domainTerms, setDomainTerms] = useState<DomainTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>(defaultFilters);
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState<SortField>('term');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const [editingMapping, setEditingMapping] = useState<SynonymMapping | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [formTermId, setFormTermId] = useState<number>(0);
  const [formTermFullForm, setFormTermFullForm] = useState('');
  const [formTermIsAbbreviation, setFormTermIsAbbreviation] = useState(false);
  const [formSynonym, setFormSynonym] = useState('');
  const [formRelType, setFormRelType] = useState('exact');
  const [formConfidence, setFormConfidence] = useState('1');
  const [formSource, setFormSource] = useState('');
  const [formVerified, setFormVerified] = useState(false);

  // "Create new term" inline fields
  const [creatingNewTerm, setCreatingNewTerm] = useState(false);
  const [newTermName, setNewTermName] = useState('');
  const [newTermFullForm, setNewTermFullForm] = useState('');
  const [newTermIsAbbreviation, setNewTermIsAbbreviation] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [mappingsResult, termsResult] = await Promise.all([
        docProcessingApi.listSynonymMappings(),
        docProcessingApi.listDomainTerms(),
      ]);
      setMappings(mappingsResult.entries);
      setDomainTerms(termsResult.entries);
    } catch (err: any) {
      setError(err.message || 'Failed to load synonym mappings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Unique values for filter dropdowns
  const relationshipTypes = useMemo(() => {
    const types = new Set(mappings.map((m) => m.relationship_type || '').filter(Boolean));
    return Array.from(types).sort();
  }, [mappings]);

  const sources = useMemo(() => {
    const srcs = new Set(mappings.map((m) => m.source || '').filter(Boolean));
    return Array.from(srcs).sort();
  }, [mappings]);

  const hasActiveColumnFilters = useMemo(() => {
    return Object.entries(columnFilters).some(([key, val]) => {
      if (key === 'verified') return val !== 'all';
      return val !== '';
    });
  }, [columnFilters]);

  // Filter & sort
  const filtered = useMemo(() => {
    let list = mappings;

    // Global search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (m) =>
          (m.term || '').toLowerCase().includes(q) ||
          m.synonym.toLowerCase().includes(q),
      );
    }

    // Column filters
    if (showColumnFilters || hasActiveColumnFilters) {
      if (columnFilters.term) {
        const q = columnFilters.term.toLowerCase();
        list = list.filter((m) => (m.term || '').toLowerCase().includes(q));
      }
      if (columnFilters.synonym) {
        const q = columnFilters.synonym.toLowerCase();
        list = list.filter((m) => m.synonym.toLowerCase().includes(q));
      }
      if (columnFilters.relationship_type) {
        list = list.filter((m) => (m.relationship_type || '') === columnFilters.relationship_type);
      }
      if (columnFilters.source) {
        list = list.filter((m) => (m.source || '') === columnFilters.source);
      }
      if (columnFilters.confidence) {
        const threshold = parseFloat(columnFilters.confidence);
        if (!isNaN(threshold)) {
          list = list.filter((m) => (m.confidence ?? 0) >= threshold);
        }
      }
      if (columnFilters.verified !== 'all') {
        const isVerified = columnFilters.verified === 'yes';
        list = list.filter((m) => (m.verified ?? false) === isVerified);
      }
      if (columnFilters.usage_count) {
        const threshold = parseInt(columnFilters.usage_count, 10);
        if (!isNaN(threshold)) {
          list = list.filter((m) => (m.usage_count ?? 0) >= threshold);
        }
      }
    }

    list = [...list].sort((a, b) => {
      let av: any, bv: any;
      if (sortField === 'term') {
        av = (a.term || '').toLowerCase();
        bv = (b.term || '').toLowerCase();
      } else {
        av = (a as any)[sortField] ?? '';
        bv = (b as any)[sortField] ?? '';
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [mappings, searchQuery, columnFilters, showColumnFilters, hasActiveColumnFilters, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const updateColumnFilter = (field: keyof ColumnFilters, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(0);
  };

  const clearAllFilters = () => {
    setColumnFilters(defaultFilters);
    setSearchQuery('');
    setCurrentPage(0);
  };

  const openEditor = (mapping: SynonymMapping | null) => {
    setCreatingNewTerm(false);
    setNewTermName('');
    setNewTermFullForm('');
    setNewTermIsAbbreviation(true);
    if (mapping) {
      setEditingMapping(mapping);
      setIsNew(false);
      setFormTermId(mapping.term_id);
      const term = domainTerms.find((t) => t.id === mapping.term_id);
      setFormTermFullForm(term?.full_form || '');
      setFormTermIsAbbreviation(term?.is_abbreviation ?? false);
      setFormSynonym(mapping.synonym);
      setFormRelType(mapping.relationship_type || 'exact');
      setFormConfidence(String(mapping.confidence ?? 1));
      setFormSource(mapping.source || '');
      setFormVerified(mapping.verified ?? false);
    } else {
      setEditingMapping({} as SynonymMapping);
      setIsNew(true);
      setFormTermId(0);
      setFormTermFullForm('');
      setFormTermIsAbbreviation(false);
      setFormSynonym('');
      setFormRelType('abbreviation');
      setFormConfidence('1');
      setFormSource('manual');
      setFormVerified(false);
      setCreatingNewTerm(true);
    }
  };

  const handleSave = async () => {
    if (!formSynonym.trim()) return;
    if (!creatingNewTerm && !formTermId) return;
    if (creatingNewTerm && !newTermName.trim()) return;

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      let termId = formTermId;

      if (creatingNewTerm && newTermName.trim()) {
        const termResult = await docProcessingApi.upsertDomainTerm({
          term: newTermName.trim().toLowerCase(),
          canonical_form: newTermFullForm.trim() || newTermName.trim(),
          is_abbreviation: newTermIsAbbreviation,
          full_form: newTermFullForm.trim() || null,
          category: newTermIsAbbreviation ? 'abbreviation' : null,
          source: 'manual',
          confidence: 1,
          verified: true,
        });
        termId = termResult.entry.id;
        setDomainTerms((prev) => [termResult.entry, ...prev]);
      } else if (!creatingNewTerm && formTermId > 0) {
        // Persist Full form / Is-abbreviation edits on the selected existing term.
        const existing = domainTerms.find((t) => t.id === formTermId);
        const nextFullForm = formTermFullForm.trim() || null;
        const fullFormChanged =
          !!existing && (existing.full_form || '') !== (nextFullForm || '');
        const isAbbrevChanged =
          !!existing && existing.is_abbreviation !== formTermIsAbbreviation;
        if (existing && (fullFormChanged || isAbbrevChanged)) {
          const termResult = await docProcessingApi.upsertDomainTerm({
            id: existing.id,
            term: existing.term,
            canonical_form: existing.canonical_form,
            is_abbreviation: formTermIsAbbreviation,
            full_form: nextFullForm,
            category: existing.category,
            source: existing.source,
            confidence: existing.confidence,
            verified: existing.verified ?? undefined,
          });
          setDomainTerms((prev) =>
            prev.map((t) => (t.id === termResult.entry.id ? termResult.entry : t))
          );
        }
      }

      const data: SynonymMappingUpsertRequest = {
        ...(editingMapping && !isNew ? { id: editingMapping.id } : {}),
        term_id: termId,
        synonym: formSynonym.trim(),
        relationship_type: formRelType || 'exact',
        confidence: parseFloat(formConfidence) || 1,
        source: formSource || 'manual',
        verified: formVerified,
      };
      const result = await docProcessingApi.upsertSynonymMapping(data);
      const saved = result.entry;

      setMappings((prev) => {
        const idx = prev.findIndex((m) => m.id === saved.id);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = saved;
          return updated;
        }
        return [saved, ...prev];
      });

      const msg = creatingNewTerm
        ? `Term "${newTermName.trim()}" created with synonym "${formSynonym.trim()}"`
        : isNew ? 'Mapping created' : 'Mapping updated';
      setSuccess(msg);
      setEditingMapping(null);
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingMapping || isNew) return;
    if (!window.confirm(`Delete synonym "${editingMapping.synonym}" for "${editingMapping.term}"?`)) return;
    const deletedId = editingMapping.id;
    setSaving(true);
    setSuccess(null);
    try {
      await docProcessingApi.deleteSynonymMapping(deletedId);
      setMappings((prev) => prev.filter((m) => m.id !== deletedId));
      setSuccess(`Synonym "${editingMapping.synonym}" deleted`);
      setEditingMapping(null);
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const SortHeader = ({ field, label, className }: { field: SortField; label: string; className?: string }) => (
    <th
      className={`px-3 py-2 font-medium cursor-pointer select-none hover:bg-gray-100 transition-colors ${className || ''}`}
      onClick={() => toggleSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {sortField === field ? (
          sortDir === 'asc' ? <ChevronUp size={12} className="text-blue-600" /> : <ChevronDown size={12} className="text-blue-600" />
        ) : (
          <ArrowUpDown size={11} className="text-gray-300" />
        )}
      </div>
    </th>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Synonym</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {filtered.length === mappings.length
              ? `${mappings.length} mappings across ${domainTerms.length} domain terms`
              : `${filtered.length} of ${mappings.length} mappings (filtered)`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search term or synonym…"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(0); }}
              className="pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-52"
            />
          </div>
          <button
            onClick={() => setShowColumnFilters((v) => !v)}
            title="Toggle column filters"
            className={`flex items-center gap-1 px-2.5 py-1.5 border rounded-md text-xs transition-colors ${
              showColumnFilters || hasActiveColumnFilters
                ? 'border-blue-400 text-blue-700 bg-blue-50 hover:bg-blue-100'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter size={13} />
            Column Filters
            {hasActiveColumnFilters && (
              <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white rounded-full text-[9px] font-bold leading-none">
                {Object.entries(columnFilters).filter(([k, v]) => {
                  if (k === 'verified') return v !== 'all';
                  return v !== '';
                }).length}
              </span>
            )}
          </button>
          {hasActiveColumnFilters && (
            <button
              onClick={clearAllFilters}
              title="Clear all filters"
              className="flex items-center gap-1 px-2 py-1.5 border border-red-200 text-red-600 rounded-md text-xs hover:bg-red-50 transition-colors"
            >
              <XCircle size={13} /> Clear
            </button>
          )}
          <button
            onClick={() => openEditor(null)}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus size={13} /> Add
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-green-700">{success}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 size={20} className="animate-spin mx-auto text-gray-400" />
            <p className="text-xs text-gray-500 mt-2">Loading synonym mappings…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-xs">
            {searchQuery || hasActiveColumnFilters ? (
              <div className="space-y-2">
                <p>No mappings match the current filters.</p>
                <button
                  onClick={clearAllFilters}
                  className="text-blue-600 hover:underline"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              'No synonym mappings found.'
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500 bg-gray-50">
                    <SortHeader field="term" label="Domain Term" />
                    <SortHeader field="synonym" label="Synonym" />
                    <SortHeader field="relationship_type" label="Type" />
                    <SortHeader field="confidence" label="Conf" />
                    <SortHeader field="source" label="Source" />
                    <SortHeader field="verified" label="Verified" />
                    <SortHeader field="usage_count" label="Uses" />
                  </tr>

                  {/* Column filter row */}
                  {showColumnFilters && (
                    <tr className="border-b border-gray-200 bg-blue-50/40">
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          placeholder="Filter…"
                          value={columnFilters.term}
                          onChange={(e) => updateColumnFilter('term', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          placeholder="Filter…"
                          value={columnFilters.synonym}
                          onChange={(e) => updateColumnFilter('synonym', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <select
                          value={columnFilters.relationship_type}
                          onChange={(e) => updateColumnFilter('relationship_type', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        >
                          <option value="">All</option>
                          {relationshipTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          placeholder="≥"
                          min="0"
                          max="1"
                          step="0.1"
                          value={columnFilters.confidence}
                          onChange={(e) => updateColumnFilter('confidence', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <select
                          value={columnFilters.source}
                          onChange={(e) => updateColumnFilter('source', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        >
                          <option value="">All</option>
                          {sources.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <select
                          value={columnFilters.verified}
                          onChange={(e) => updateColumnFilter('verified', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        >
                          <option value="all">All</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          placeholder="≥"
                          min="0"
                          value={columnFilters.usage_count}
                          onChange={(e) => updateColumnFilter('usage_count', e.target.value)}
                          className="w-full text-[11px] border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                        />
                      </td>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {paginated.map((m) => (
                    <tr
                      key={m.id}
                      onClick={() => openEditor(m)}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-3 py-2 font-medium text-gray-900">{m.term || `#${m.term_id}`}</td>
                      <td className="px-3 py-2 text-gray-700">{m.synonym}</td>
                      <td className="px-3 py-2">
                        <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px]">
                          {m.relationship_type || 'exact'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-500">{m.confidence != null ? m.confidence.toFixed(2) : '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{m.source || '—'}</td>
                      <td className="px-3 py-2 text-center">
                        {m.verified ? <CheckCircle size={13} className="text-green-500 inline" /> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-500">{m.usage_count ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <span className="text-xs text-gray-500">
                Showing {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
                {filtered.length !== mappings.length && (
                  <span className="text-gray-400"> (filtered from {mappings.length})</span>
                )}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(0)}
                  disabled={currentPage === 0}
                  className="px-1.5 py-1 rounded text-xs hover:bg-gray-200 disabled:opacity-30"
                  title="First page"
                >
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs text-gray-600 px-2">
                  {currentPage + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                >
                  <ChevronRight size={14} />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages - 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="px-1.5 py-1 rounded text-xs hover:bg-gray-200 disabled:opacity-30"
                  title="Last page"
                >
                  »»
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit / Create Modal */}
      {editingMapping && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {isNew ? 'Add Synonym' : 'Edit Synonym'}
                </h3>
                {!isNew && (
                  <p className="text-xs text-gray-500 mt-0.5">ID: {editingMapping.id}</p>
                )}
              </div>
              <button onClick={() => setEditingMapping(null)} className="p-1 hover:bg-gray-100 rounded">
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            {/* Form */}
            <div className="px-5 py-4 space-y-3">
              {/* Domain Term — select existing OR create new */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Domain Term <span className="text-red-500">*</span>
                </label>
                {!creatingNewTerm ? (
                  <div className="space-y-1.5">
                    <select
                      value={formTermId}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '__new__') {
                          setCreatingNewTerm(true);
                          setFormTermId(0);
                          setFormTermFullForm('');
                          setFormTermIsAbbreviation(false);
                        } else {
                          const id = Number(val);
                          setFormTermId(id);
                          const term = domainTerms.find((t) => t.id === id);
                          setFormTermFullForm(term?.full_form || '');
                          setFormTermIsAbbreviation(term?.is_abbreviation ?? false);
                        }
                      }}
                      className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={0} disabled>Select a term…</option>
                      <option value="__new__" className="font-medium text-blue-600">+ Create new term…</option>
                      {domainTerms.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.term} {t.category ? `(${t.category})` : ''}
                        </option>
                      ))}
                    </select>
                    {formTermId > 0 && (
                      <>
                        <input
                          type="text"
                          value={formTermFullForm}
                          onChange={(e) => setFormTermFullForm(e.target.value)}
                          className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Full form (e.g. Special Rate Request)"
                        />
                        <label className="flex items-center gap-1.5 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            checked={formTermIsAbbreviation}
                            onChange={(e) => setFormTermIsAbbreviation(e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          Is abbreviation / acronym
                        </label>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide">New Term</span>
                      {domainTerms.length > 0 && (
                        <button
                          type="button"
                          onClick={() => { setCreatingNewTerm(false); setFormTermId(domainTerms[0].id); }}
                          className="text-[10px] text-blue-600 hover:underline"
                        >
                          Select existing instead
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={newTermName}
                      onChange={(e) => setNewTermName(e.target.value)}
                      className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      placeholder="e.g. SRR, BKG, MCN"
                      autoFocus
                    />
                    <input
                      type="text"
                      value={newTermFullForm}
                      onChange={(e) => setNewTermFullForm(e.target.value)}
                      className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      placeholder="Full form (e.g. Special Rate Request)"
                    />
                    <label className="flex items-center gap-1.5 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={newTermIsAbbreviation}
                        onChange={(e) => setNewTermIsAbbreviation(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      Is abbreviation / acronym
                    </label>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Synonym <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formSynonym}
                  onChange={(e) => setFormSynonym(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. bill of lading, shipping document"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Relationship Type</label>
                  <select
                    value={formRelType}
                    onChange={(e) => setFormRelType(e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="exact">exact</option>
                    <option value="related">related</option>
                    <option value="broader">broader</option>
                    <option value="narrower">narrower</option>
                    <option value="abbreviation">abbreviation</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Source</label>
                  <input
                    type="text"
                    value={formSource}
                    onChange={(e) => setFormSource(e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. manual"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    checked={formVerified}
                    onChange={(e) => setFormVerified(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Verified
                </label>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Confidence ({formConfidence})
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={formConfidence}
                  onChange={(e) => setFormConfidence(e.target.value)}
                  className="w-full"
                />
              </div>

              {!isNew && editingMapping.created_at && (
                <div className="bg-gray-50 rounded-lg px-3 py-2 text-[10px] text-gray-500 space-y-0.5">
                  <p>Created: {new Date(editingMapping.created_at).toLocaleString()}</p>
                  <p>Usage count: {editingMapping.usage_count ?? 0}</p>
                  <p>Success rate: {editingMapping.success_rate != null ? `${(editingMapping.success_rate * 100).toFixed(0)}%` : '—'}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div>
                {!isNew && (
                  <button
                    onClick={handleDelete}
                    disabled={saving}
                    className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-md text-xs transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditingMapping(null)}
                  className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-md text-xs transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !formSynonym.trim() || (!creatingNewTerm && !formTermId) || (creatingNewTerm && !newTermName.trim())}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
